import { LoadingContainer } from 'app/components/LoadingContainer';
import RenderIf from 'app/components/RenderIf';
import { getDashboardRevenueApi } from 'app/services/homePageApi';
import _ from 'lodash';
import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { buildFullNameMap } from '../constants';
import {
  DASHBOARD_REPORT_TYPE,
  groupRevenueRows,
  useDashboardReport,
} from '../hooks/useDashboardReport';
import { useParkNameMap } from '../hooks/useParkNameMap';
import ReportEmpty from './ReportEmpty';
import {
  AxisTick,
  ChartScroll,
  formatCurrencyShort,
  ReportCardHeader,
  ReportTooltip,
  useIsReportMobile,
} from './reportChartUtils';
import ReportPeriodFilter from './ReportPeriodFilter';

const BAR_COLOR = 'var(--color-9)';
const LINE_COLOR = 'var(--color-14)';

interface IParkRevenueReportProps {
  translations: any;
}

export function ParkRevenueReport(props: IParkRevenueReportProps) {
  const { translations } = props;
  const { parkById, isLoadingPark } = useParkNameMap();
  const {
    rows,
    period,
    onChangePeriod,
    isLoading: isLoadingRevenue,
  } = useDashboardReport({
    actionApi: getDashboardRevenueApi,
    type: DASHBOARD_REPORT_TYPE.PARK,
  });

  const data = useMemo(
    () =>
      groupRevenueRows(
        rows,
        row => String(row?.parkId),
        parkId => {
          const park = parkById[parkId];
          const fullName = park?.name || parkId;
          return { name: park?.shortName || fullName, fullName };
        },
      ),
    [rows, parkById],
  );

  const chartData = useMemo(() => {
    if (_.isEmpty(data)) return data;
    const totalName = translations?.['b2b.txt.total'] ?? '';
    return [
      {
        name: totalName,
        fullName: totalName,
        revenue: _.sumBy(data, 'revenue'),
        ticketCount: _.sumBy(data, 'ticketCount'),
      },
      ...data,
    ];
  }, [data, translations]);

  const fullNameByShortName = useMemo(
    () => buildFullNameMap(chartData),
    [chartData],
  );
  const isLoading = isLoadingRevenue || isLoadingPark;
  const isMobile = useIsReportMobile();
  const chartHeight = isMobile ? 300 : 360;

  return (
    <div className="box-report-card">
      <ReportCardHeader>
        <div className="report-card-title">
          <div className="sub-title">
            {translations?.['b2b.txt.revenueByPark'] ?? ''}
          </div>
          <div className="report-card-note">
            {translations?.['b2b.txt.usedTicketNote'] ?? ''}
          </div>
        </div>
        <div className="report-card-actions">
          <ReportPeriodFilter
            translations={translations}
            period={period}
            onChange={onChangePeriod}
          />
        </div>
      </ReportCardHeader>
      <LoadingContainer loading={isLoading} minHeight={`${chartHeight}px`}>
        <RenderIf
          condition={!_.isEmpty(data)}
          fallback={
            <ReportEmpty translations={translations} height={chartHeight} />
          }
        >
          <ChartScroll $itemCount={chartData.length}>
            <div className="chart-scroll-inner">
              <ResponsiveContainer width="100%" height={chartHeight}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 8, bottom: 8, left: 0 }}
                >
                  <defs>
                    <filter
                      id="lineDotShadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="0"
                        dy="1"
                        stdDeviation="1.5"
                        floodColor="#0f172a"
                        floodOpacity="0.25"
                      />
                    </filter>
                  </defs>
                  <CartesianGrid
                    stroke="#eaeaea"
                    strokeDasharray="4 4"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    height={36}
                    tickMargin={4}
                    tick={
                      <AxisTick fullNameByShortName={fullNameByShortName} />
                    }
                  />
                  <YAxis
                    yAxisId="revenue"
                    orientation="left"
                    axisLine={false}
                    tickLine={false}
                    width={isMobile ? 46 : 60}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={value =>
                      value === 0 ? '(VND)' : formatCurrencyShort(value)
                    }
                  />
                  <YAxis
                    yAxisId="ticket"
                    orientation="right"
                    axisLine={false}
                    tickLine={false}
                    width={isMobile ? 46 : 60}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    tickFormatter={value =>
                      value === 0
                        ? `(${translations?.['b2c.txt.ticket']})`
                        : formatCurrencyShort(value)
                    }
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255, 122, 47, 0.08)' }}
                    content={<ReportTooltip translations={translations} />}
                  />
                  <Legend
                    content={<ChartLegend />}
                    verticalAlign="bottom"
                    height={36}
                    wrapperStyle={{ paddingTop: 16 }}
                  />
                  <Bar
                    yAxisId="revenue"
                    dataKey="revenue"
                    fill={BAR_COLOR}
                    radius={[4, 4, 0, 0]}
                    barSize={22}
                    name={translations?.['b2b.txt.parkRevenue'] ?? ''}
                  />
                  <Line
                    yAxisId="ticket"
                    type="linear"
                    dataKey="ticketCount"
                    stroke={LINE_COLOR}
                    strokeWidth={2}
                    dot={{
                      r: 4,
                      fill: LINE_COLOR,
                      stroke: '#fff',
                      strokeWidth: 2,
                      filter: 'url(#lineDotShadow)',
                    }}
                    activeDot={{
                      r: 6,
                      fill: LINE_COLOR,
                      stroke: '#fff',
                      strokeWidth: 2.5,
                      filter: 'url(#lineDotShadow)',
                    }}
                    name={translations?.['b2b.txt.ticketsSold'] ?? ''}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </ChartScroll>
        </RenderIf>
      </LoadingContainer>
    </div>
  );
}

interface IChartLegendProps {
  payload?: any[];
}

const ChartLegend = ({ payload }: IChartLegendProps) => (
  <div className="d-flex flex-wrap justify-content-center gap-4">
    {payload?.map(entry => (
      <div key={entry.value} className="d-flex align-items-center gap-2">
        <span
          style={{
            width: entry.type === 'line' ? 14 : 8,
            height: entry.type === 'line' ? 3 : 8,
            borderRadius: 8,
            backgroundColor: entry.color,
          }}
        />
        <span style={{ fontSize: 12 }}>{entry.value}</span>
      </div>
    ))}
  </div>
);

export default ParkRevenueReport;
